import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'

interface AssetThumbnailProps {
  assetId: string
  assetType: 'pdf' | 'video'
  title: string
  onClick: () => void
  width?: number
  height?: number
}

// A real visual thumbnail, not just a text label or button — a small, non-interactive
// render of the actual file (page 1 for a PDF, the first frame for a video), click-through
// to the full SecureAssetViewer. Uses the exact same signed-URL mechanism
// (get-curriculum-asset-url) as the full viewer — a thumbnail is not a separate, lower-
// trust asset, just a small rendering of the same short-lived stream, so it carries the
// same "never a permanent/public URL" property.
export function AssetThumbnail({ assetId, assetType, title, onClick, width = 140, height = 90 }: AssetThumbnailProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setFailed(false)
    supabase.functions.invoke('get-curriculum-asset-url', { body: { assetId } }).then(({ data, error }) => {
      if (cancelled) return
      if (error || data?.error || !data?.url) { setFailed(true); return }
      setUrl(data.url)
    })
    return () => { cancelled = true }
  }, [assetId])

  const boxStyle: React.CSSProperties = {
    width, height, borderRadius: 8, overflow: 'hidden', position: 'relative',
    background: '#1B2A4A', cursor: 'pointer', flexShrink: 0,
    border: '1px solid rgba(27,42,74,0.12)',
  }

  if (failed) {
    return (
      <div style={{ ...boxStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClick}>
        <span style={{ font: '500 11px Inter, sans-serif', color: 'rgba(255,255,255,0.5)' }}>
          {assetType === 'pdf' ? 'PDF' : 'Video'}
        </span>
      </div>
    )
  }

  if (!url) {
    return <div style={{ ...boxStyle, background: 'rgba(27,42,74,0.06)' }} />
  }

  return (
    <div style={boxStyle} onClick={onClick} title={title}>
      {assetType === 'pdf' ? (
        // A real rendering of page 1, scaled down to thumbnail size via a larger iframe
        // viewport shrunk with CSS transform (browsers lay out an iframe's PDF viewer at
        // its own natural size, so scaling the element directly — rather than sizing the
        // iframe itself to 140x90 — is what actually keeps the page readable in miniature
        // instead of just cropping its top-left corner).
        <div style={{ width: width * 3, height: height * 3, transform: `scale(${1 / 3})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
          <iframe title={title} src={`${url}#toolbar=0&navpanes=0&view=FitH`} style={{ width: '100%', height: '100%', border: 'none' }} tabIndex={-1} />
        </div>
      ) : (
        <video
          ref={videoRef}
          src={url}
          muted
          playsInline
          preload='metadata'
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
          // A freshly loaded <video> often paints a blank first frame until it's actually
          // decoded one — nudging currentTime forces the browser to decode and display a
          // real frame instead of a black box.
          onLoadedMetadata={() => { if (videoRef.current) videoRef.current.currentTime = 0.1 }}
        />
      )}
      {assetType === 'video' && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '9px solid #fff', marginLeft: 2 }} />
          </div>
        </div>
      )}
      {/* A plain, untransformed click-catcher on top of everything — the PDF thumbnail's
          scaled-down iframe (via CSS transform) sits in its own stacking/paint context,
          and relying on a click simply "passing through" a pointer-events:none ancestor
          to reach this div's own onClick proved unreliable in practice. This overlay is
          never itself transformed, so hit-testing on it is unambiguous regardless. */}
      <div style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} onClick={onClick} />
    </div>
  )
}
